import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Wrench, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@ferreteriaelbodegon.com");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Sesión iniciada");
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr,1fr] bg-background">
      {/* Left visual */}
      <div className="hidden lg:flex relative overflow-hidden bg-[#0F172A] grid-bg-dark">
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-md grid place-items-center">
              <Wrench className="w-5 h-5" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-white/60 font-semibold">Suite Modular</div>
              <div className="text-lg font-bold">Amelia</div>
            </div>
          </div>

          <div>
            <div className="overline text-white/60">Gestión Empresarial</div>
            <h2 className="text-5xl font-black tracking-tight leading-[1.05] mt-3">
              Inventario, ventas<br/>
              y <span className="text-primary">contabilidad</span><br/>
              en un solo lugar.
            </h2>
            <p className="text-white/60 max-w-md mt-6 text-sm leading-relaxed">
              Suite modular adaptable a ferreterías, restaurantes, tiendas y
              servicios. Cumple con la DGII (NCF y e-CF). POS rápido, control
              de stock, contabilidad doble entrada y reportes en tiempo real.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {["POS + CxC", "Contabilidad", "DGII e-CF"].map((t, i) => (
                <div key={t} className="border border-white/15 rounded-md px-3 py-2 text-xs">
                  <div className="font-mono text-[10px] text-white/50">.0{i + 1}</div>
                  <div className="font-semibold mt-0.5">{t}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-xs text-white/40 font-mono">
            Amelia · v1.0 · DOP / USD
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form
          onSubmit={onSubmit}
          className="w-full max-w-md"
          data-testid="login-form"
        >
          <div className="overline mb-2">Acceso</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">
            Inicia sesión
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Bienvenido de nuevo. Ingresa tus credenciales para continuar.
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-bold tracking-wider uppercase">
                Email
              </Label>
              <Input
                id="email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@ferreteriaelbodegon.com"
                required
                data-testid="login-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label htmlFor="password" className="text-xs font-bold tracking-wider uppercase">
                  Contraseña
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
              disabled={submitting}
              data-testid="login-submit-button"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Entrar
            </Button>
          </div>

          <div className="mt-8 p-3 bg-muted rounded-md text-xs text-muted-foreground">
            <div className="overline mb-1">Demo</div>
            Usuario administrador:&nbsp;
            <span className="font-mono">admin@ferreteriaelbodegon.com</span> · contraseña:&nbsp;
            <span className="font-mono">Admin2026!</span>
          </div>
        </form>
      </div>
    </div>
  );
}
